import {
  Backdrop,
  Box,
  Container,
  Fade,
  FormControl,
  FormLabel,
  Modal,
  Typography,
  useTheme,
} from '@mui/material';
import articlesData from './articles/index.json';
import { Link as RouterLink, LinkProps } from 'react-router-dom';
import {
  ButtonTertiaryIcon,
  GlassButton,
  GlassColors,
  GlassTextField,
} from '../../components/glassmorphism/GlassmorphismStyles';

import React, { useState } from 'react';
import { ContactUsForm } from '../Support/ContactUsForm';
import { SecondaryButton } from '../../components/GetStarted/GetStarted';

import { ReactComponent as HeadPhone } from '../../assets/icons/info/headphone.svg';
import { ReactComponent as CloseIcon } from '../../assets/icons/nav/close.svg';
const LinkBehavior = React.forwardRef<HTMLAnchorElement, LinkProps>(
  (props, ref) => <RouterLink ref={ref} {...props} />,
);
LinkBehavior.displayName = 'LinkBehavior';
type QAItem = { question: string; answer: string };
type Article = {
  id: string | number;
  title: string;
  excerpt: string;
  slug: string;
  updatedAt: string;
  qa?: QAItem[];
  html: string;
  subtitle?: string;
};

const launcheArticles = articlesData.articles;

function getFilteredArticles(articles: Article[], query: string) {
  const clauses = [...articles];

  const filteredArticles = clauses.filter((clause) => {
    return (
      clause.title.toLowerCase().includes(query.trim().toLowerCase()) ||
      clause.excerpt.toLowerCase().includes(query.trim().toLowerCase()) ||
      clause.html.toLowerCase().includes(query.trim().toLowerCase())
    );
  });

  return filteredArticles;
}

export const HelpBlogPage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  //modal state
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const [query, setQuery] = useState('');

  const visibleArticles = getFilteredArticles(launcheArticles, query);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        background: isDark ? '#1A1A1A' : '#E5E5DC',
        paddingTop: { xs: '80px', sm: '120px' },
        paddingBottom: { xs: '24px', sm: '60px' },
      }}
    >
      <Container
        maxWidth='lg'
        sx={{
          width: '956px',
          maxWidth: '90%',
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: '24px', sm: '48px' },
          p: 0,
        }}
      >
        {/* Page header */}
        <Box>
          <Typography
            variant='h4'
            component='h1'
            sx={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: { xs: 600, sm: 500 },
              fontSize: { xs: '34px', sm: '60px' },

              color: isDark ? GlassColors.accent : GlassColors.secondary,
              lineHeight: { xs: '124%', sm: '120%' },
              letterSpacing: { xs: '0.25px', sm: '-.5px' },
              marginBottom: { xs: '16px', sm: '24px' },
              marginTop: { xs: '16px', sm: '16px' },
              textAlign: 'center',
            }}
          >
            Help Blog
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: { xs: 14, sm: 16 },
              fontWeight: 700,
              letterSpacing: { xs: '.1px', sm: '.15px' },
              lineHeight: { xs: '157%', sm: '175%' },

              color: isDark
                ? GlassColors.dark.textPrimary
                : GlassColors.light.textPrimary,
              textAlign: 'center',
            }}
          >
            Browse guides, tutorials, and tips for Clients and Providers.
          </Typography>
        </Box>

        {/*  Filter Article input */}
        <Box sx={{ maxWidth: '760px', width: '100%', margin: 'auto' }}>
          <FormControl fullWidth>
            <FormLabel
              htmlFor='filterArticle'
              sx={{
                color: isDark
                  ? GlassColors.dark.textSecondary
                  : GlassColors.textPrimary,
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                fontWeight: 400,
              }}
            >
              Search for answers
            </FormLabel>
            <GlassTextField
              onChange={(e) => setQuery(e.target.value)}
              name='filterArticle'
              id='filterArticle'
              fullWidth
              placeholder='e.g. "payments", "cancellation"'
            />
          </FormControl>
        </Box>

        {/* List of articles */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {visibleArticles.map((a) => (
            <Box
              component='article'
              key={a.id}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                padding: '24px',
                borderRadius: '32px',
                fontFamily: 'roboto, sans-serif',
                color: isDark
                  ? GlassColors.dark.textSecondary
                  : GlassColors.light.textSecondary,
                boxShadow: `
                  -2.82px -2.82px 8.49px 0px #FFFFFF4D,
                  14.15px 14.15px 22.64px -5.37px #0000001A,
                  -7.54px -7.54px 8.66px 0px #FFFFFF33,
                  5px 5px 24px -11.7px #00000033
                `,
              }}
            >
              <Typography
                variant='h4'
                sx={{
                  fontFamily: 'montserrat, sans-serif',
                  fontWeight: { xs: 800, sm: 700 },
                  fontSize: { xs: '20px', sm: '24px' },
                  lineHeight: { xs: '160%', sm: '133%' },
                  letterSpacing: { xs: '.15px', sm: 'unset' },
                }}
              >
                {a.title}
              </Typography>

              <Typography
                className='line-clamp-3-xs-2'
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  whiteSpace: 'pre-line',
                  fontSize: { xs: '14px', sm: '16px' },
                  lineHeight: { xs: '143%', sm: '150%' },
                  letterSpacing: { xs: '.17px', sm: '.15px' },
                }}
              >
                {a.excerpt}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: '10px',
                }}
              >
                <Box
                  component='span'
                  sx={{
                    fontSize: { xs: '14px', sm: '16px' },
                    fontWeight: 700,
                    lineHeight: { xs: '157%', sm: '175%' },
                    letterSpacing: { xs: '.1px', sm: '.15px' },
                    alignSelf: 'start',
                    color: isDark ? GlassColors.accent : GlassColors.secondary,
                  }}
                >
                  Updated {a.updatedAt}
                </Box>

                <GlassButton
                  sx={{ ml: 'auto' }}
                  component={RouterLink}
                  to={`/help-blog/${a.slug}`}
                >
                  Read full article
                </GlassButton>
              </Box>
            </Box>
          ))}

          {(!visibleArticles || visibleArticles.length === 0) && (
            <p style={{ textAlign: 'center' }}>No results found</p>
          )}
        </Box>

        {/* Contact block */}
        <Box
          sx={{
            p: '16px 24px 24px',
            maxWidth: '748px',
            width: '100%',
            textAlign: 'center',
            margin: 'auto',
            borderRadius: '32px',
            boxShadow: `
              -2.82px -2.82px 8.49px 0px #FFFFFF4D,
              14.15px 14.15px 22.64px -0.37px #0000001A,
              -17.54px -17.54px 28.66px 0px #FFFFFF0D,
              10px 10px 28.66px -11.7px #00000033,
              1.58px 1.58px 1.7px 0px #FFFFFF inset,
              -2.07px -2.64px 4.52px -1.51px #0000004D inset
            `,
          }}
        >
          <Typography
            sx={{
              fontFamily: 'montserrat, sans-serif',
              fontWeight: 700,
              fontSize: '24px',
              lineHeight: '133%',
              mb: '24px',
              color: isDark
                ? GlassColors.dark.textPrimary
                : GlassColors.light.textPrimary,
            }}
          >
            Can't find what you're looking for?
          </Typography>
          <SecondaryButton
            onClick={handleOpen}
            sx={{
              fontFamily: '"Montserrat", sans-serif',
              color: '#fff',
              fontSize: { xs: 14, sm: 16 },
              lineHeight: { xs: '32px', sm: '32px' },
              height: { xs: '40px', sm: '48px' },
              minWidth: { xs: '177px' },
              padding: '8px 24px',
              width: 'auto',
              borderRadius: '24px',
              border: 0,
              m: 'auto',
              backgroundColor: isDark
                ? GlassColors.dark.iconBtnBackground
                : GlassColors.light.iconBtnBackground,

              '& svg': {
                width: { xs: '20px', sm: '24px' },
              },
            }}
          >
            <HeadPhone />
            Contact support
          </SecondaryButton>
          <Modal
            aria-labelledby='transition-modal-title'
            aria-describedby='transition-modal-description'
            open={open}
            onClose={handleClose}
            closeAfterTransition
            slots={{ backdrop: Backdrop }}
            slotProps={{
              backdrop: {
                timeout: 200,
              },
            }}
            sx={{ overflowY: 'auto' }}
          >
            <Fade in={open}>
              <Box
                sx={{
                  position: 'absolute',
                  left: '50%',
                  py: { xs: '80px', sm: '120px' },
                  overflowY: 'auto',
                  transform: 'translateX(-50%)',
                  width: '90%',
                  maxWidth: '760px',
                  bgcolor: 'transparent',
                  boxShadow: 'none',
                  borderRadius: '32px',
                  pb: '32px',
                }}
              >
                <ButtonTertiaryIcon
                  onClick={() => {
                    handleClose();
                  }}
                  sx={{
                    position: 'absolute',
                    zIndex: 1,
                    top: { xs: '96px', sm: '136px' },
                    right: '16px',
                  }}
                >
                  <CloseIcon />
                </ButtonTertiaryIcon>
                <ContactUsForm />
              </Box>
            </Fade>
          </Modal>
        </Box>
      </Container>
    </Box>
  );
};
